import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icon';
import { AvatarButton } from './AvatarButton';

/**
 * Opció del submenú horitzontal de la capçalera (§10.4): les subopcions de l'ítem de la navbar o de
 * l'element obert (una casa, una oportunitat). Amb `to`, és un enllaç; amb `onSelect`, un filtre.
 */
export interface SubmenuItem {
  id: string;
  label: string;
  to?: string;
  onSelect?: () => void;
  active: boolean;
  /** Recompte opcional (p. ex. quantes n'hi ha), mai decoratiu (§10.1). */
  count?: number;
  disabled?: boolean;
}

/** Posició de scroll de cada entrada de l'historial: enrere torna on eres (§10.4). */
const scrollByKey = new Map<string, number>();

/**
 * Una pantalla dins la carcassa (§10.4), amb l'estructura d'una app: capçalera fixa a dalt,
 * zona central que és l'única que fa scroll i, si n'hi ha, barra d'accions fixa a baix.
 * Amb `back`, és una pantalla de detall: fletxa enrere (obligatòria a la PWA d'iOS).
 */
export function Screen({ title, back, actions, wide, submenu, submenuLabel, children }: {
  title: string;
  /** Submenú horitzontal, fix a la capçalera. */
  submenu?: readonly SubmenuItem[];
  submenuLabel?: string;
  /** Adreça on tornar si no hi ha historial dins l'app (p. ex. s'ha entrat per un QR). */
  back?: string;
  actions?: ReactNode;
  wide?: boolean;
  children: ReactNode;
}) {
  const { t } = useTranslation('shell');
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const body = useRef<HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);

  // Endavant comença a dalt; enrere (POP) torna a la posició que tenia aquella entrada.
  // Si el contingut encara és curt (s'estan carregant dades), s'espera que creixi; si mentrestant
  // l'usuari ja fa scroll, es deixa córrer.
  useLayoutEffect(() => {
    const el = body.current;
    if (!el) return;
    const target = navigationType === 'POP' ? (scrollByKey.get(location.key) ?? 0) : 0;
    el.scrollTop = target;
    let pending: number | null = target > 0 && el.scrollTop < target ? target : null;
    const content = el.firstElementChild;
    const observer = new ResizeObserver(() => {
      if (pending === null) return;
      el.scrollTop = pending;
      if (el.scrollTop >= pending - 1) pending = null;
    });
    if (pending !== null && content) observer.observe(content);
    const cancel = () => { pending = null; };
    el.addEventListener('wheel', cancel, { passive: true });
    el.addEventListener('touchstart', cancel, { passive: true });
    const giveUp = window.setTimeout(cancel, 5000);
    const key = location.key;
    // Només es desa amb scroll de veritat: una pantalla provisional («Carregant…») de la mateixa
    // entrada no ha de sobreescriure la posició que s'ha de restaurar quan arribin les dades.
    const onScroll = () => {
      scrollByKey.set(key, el.scrollTop);
      setScrolled(el.scrollTop > 8);
    };
    setScrolled(el.scrollTop > 8);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      el.removeEventListener('wheel', cancel);
      el.removeEventListener('touchstart', cancel);
      observer.disconnect();
      window.clearTimeout(giveUp);
    };
  }, [location.key, navigationType]);

  useEffect(() => {
    document.title = `${title} · ${t('common:appName')}`;
  }, [title, t]);

  return (
    <div className={`screen${actions ? ' has-actions' : ''}`}>
      <header className={`screen-header glass${scrolled ? ' is-scrolled' : ''}`}>
        {back !== undefined ? (
          <button
            type="button"
            className="icon-button"
            aria-label={t('back')}
            // location.key === 'default': primera pantalla de la sessió (QR, enllaç); no hi ha enrere dins l'app.
            onClick={() => (location.key === 'default' ? navigate(back) : navigate(-1))}
          >
            <Icon name="arrowLeft" />
          </button>
        ) : (
          <a className="logo-mark" href="/" aria-label={t('homeLink')}>
            <span aria-hidden="true">B</span>
          </a>
        )}
        <h1 className="screen-title">{title}</h1>
        <AvatarButton />
        {submenu && submenu.length > 0 && <Submenu items={submenu} label={submenuLabel ?? title} />}
      </header>
      <main ref={body} className="screen-body" data-scroll-root>
        <div className={`screen-content${wide ? ' wide' : ''}`}>{children}</div>
      </main>
      {/* Accions de la pantalla: una barra que sura just damunt la navbar, que no s'amaga mai. */}
      {actions && <div className="action-bar glass">{actions}</div>}
    </div>
  );
}

/** Submenú: una fila que llisca en horitzontal al mòbil; l'opció activa sempre a la vista. */
function Submenu({ items, label }: { items: readonly SubmenuItem[]; label: string }) {
  const row = useRef<HTMLElement>(null);
  const activeId = items.find((i) => i.active)?.id;
  useEffect(() => {
    const box = row.current;
    const el = box?.querySelector<HTMLElement>('[aria-current="true"]');
    if (box && el) box.scrollLeft = el.offsetLeft - (box.clientWidth - el.clientWidth) / 2;
  }, [activeId]);
  return (
    <nav ref={row} className="submenu" aria-label={label}>
      {items.map((i) => {
        const content = <>{i.label}{i.count !== undefined && <span className="submenu-count">{i.count}</span>}</>;
        const cls = `submenu-item${i.active ? ' is-active' : ''}`;
        return i.to && !i.disabled ? (
          <Link key={i.id} to={i.to} replace className={cls} aria-current={i.active ? 'true' : undefined}>{content}</Link>
        ) : (
          <button key={i.id} type="button" className={cls} aria-current={i.active ? 'true' : undefined} disabled={i.disabled} onClick={i.onSelect}>{content}</button>
        );
      })}
    </nav>
  );
}
